import { compose, connectWithHammock, ENDPOINTS, useActions } from 'hammock';
import React, { useEffect, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import styled from 'styled-components';

import { ContentSegmentDocumentMetadata, ContentSegments } from '@candu-ai/content/types';
import { useEventing } from 'commons/eventing';
import { moveArray } from 'commons/utils';
import { DEFAULT_SEGMENT } from 'dashboard/pages/Content/constants';
import { useSegmentDocumentActions } from 'dashboard/pages/Content/pages/Notebook/actions';

import ArchivedDocumentCard from './ArchivedDocumentCard';
import DeleteDocument from './DeleteDocument';
import SegmentActionModal from './SegmentActionModal';
import { SegmentCard } from './SegmentCard';

const Container = styled.div`
  flex: 1;
  overflow: auto;
  padding: 2rem 4rem;
  width: 100%;
`;

const SegmentsContainer = styled.div`
  box-sizing: content-box;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  position: relative;

  > div {
    margin-bottom: 1rem;
    margin-right: 1rem;
  }
`;

const CardContainer = styled.div`
  position: relative;
`;

interface Props {
  contentId: string;
  applicationId: string;
  resource: any[];
  archiveDocuments: any[];
  contentSegment: ContentSegments;
  isArchived?: boolean;
  onChangeSegment: (segmentId: string) => void;
}

const SegmentsContentImpl = ({
  isArchived,
  applicationId,
  archiveDocuments,
  resource,
  contentSegment,
  contentId,
  onChangeSegment,
}: Props) => {
  const { updateSegment } = useSegmentDocumentActions();
  const [segments, setSegments] = useState(contentSegment.segmentDocuments);
  const [deletingDocument, setDeletingDocument] = useState<number | null>(null);
  const [restoreDocument, setRestoreDocument] = useState<number | null>(null);
  const archiveDocumentAction = useActions(ENDPOINTS.contentDocumentArchive) as any;

  const linkedSegments = segments.map(({ segmentId }: { segmentId?: string }) => segmentId);
  const availableSegments = resource.filter(
    ({ id, archivedAt }: { id: string; archivedAt?: string }) =>
      !linkedSegments.includes(id) && !archivedAt,
  );

  const eventing = useEventing();
  useEffect(() => {
    eventing.screen('editor.view.segmentContent');
  }, []);

  const onUpdate = ({
    currentIndex,
    destinationIndex,
  }: {
    currentIndex: number;
    destinationIndex: number;
  }) => {
    const changedSegment: any = contentSegment.segmentDocuments.find(
      (segment, index) => index === currentIndex,
    );

    if (!changedSegment && !changedSegment?.contentSegmentDocumentId) {
      return;
    }

    if (changedSegment.sortIndex === destinationIndex) {
      return;
    }

    setSegments(moveArray(segments, currentIndex, destinationIndex));

    updateSegment(
      {
        ...changedSegment,
        id: changedSegment.contentSegmentDocumentId,
        contentId,
        sortIndex: destinationIndex,
      },
      changedSegment.contentSegmentDocumentId,
    );
  };

  const deleteArchivedDocument = () => archiveDocumentAction.delete({ id: deletingDocument });

  const onRestore = (segmentId: string) => {
    archiveDocumentAction
      .action({
        name: 'restore',
        body: {
          id: restoreDocument,
          segmentId,
        },
        onSuccessNotification: { message: 'Document Successfully Restored' },
        invalidates: {
          [ENDPOINTS.contentSegments]: { id: contentId },
        },
      })
      .then(() => {
        onChangeSegment(segmentId);
      });
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <Container>
        <SegmentsContainer>
          {!isArchived &&
            segments.map((segmentDocument: ContentSegmentDocumentMetadata, index: number) => (
              <CardContainer key={segmentDocument.segmentId}>
                <SegmentCard
                  onUpdate={onUpdate}
                  index={index}
                  disabled={isArchived}
                  documentId={segmentDocument.documentId}
                  onSelectSegment={onChangeSegment}
                  segment={segmentDocument}
                  applicationId={applicationId}
                  segmentExists={!!resource.find(({ id }) => id === segmentDocument.segmentId)}
                />
              </CardContainer>
            ))}

          {!isArchived && contentSegment.defaultDocumentId && (
            <SegmentCard
              {...contentSegment}
              segmentExists
              applicationId={applicationId}
              onSelectSegment={onChangeSegment}
              index={contentSegment.segmentDocuments.length}
              documentId={contentSegment.defaultDocumentId}
              segment={{
                name: 'Unassigned users',
                lastModifiedAt: contentSegment.defaultDocumentLastModifiedAt,
              }}
            />
          )}

          {isArchived &&
            archiveDocuments.map((archiveDocument: any) => (
              // TODO: look into getting a version id for the document here
              <ArchivedDocumentCard
                key={archiveDocument.documentId}
                archiveDocumentId={archiveDocument.id}
                documentId={archiveDocument.documentId}
                applicationId={applicationId}
                onDeleteDocument={(id) => setDeletingDocument(id)}
                onRestoreDocument={(id) => setRestoreDocument(id)}
              />
            ))}
          {isArchived && !!restoreDocument ? (
            <SegmentActionModal
              segmentList={[
                ...availableSegments,
                ...(!contentSegment.defaultDocumentId ? [DEFAULT_SEGMENT] : []),
              ]}
              toggle={() => setRestoreDocument(null)}
              onAction={onRestore}
              actionText="Restore Content"
            />
          ) : null}
        </SegmentsContainer>
        <DeleteDocument
          isOpen={!!deletingDocument}
          toggle={() => setDeletingDocument(null)}
          onDelete={deleteArchivedDocument}
        />
      </Container>
    </DndProvider>
  );
};

const withSegments = connectWithHammock(() => ({
  endpoint: ENDPOINTS.segmentsDefinition,
}));

const withArchivedSegments = connectWithHammock(({ contentId }) => ({
  resourceName: 'archiveDocuments',
  endpoint: ENDPOINTS.contentDocumentArchive,
  query: { finder: 'byContentId', id: contentId },
}));

const withSegmentConnections = connectWithHammock(({ contentId }) => ({
  resourceName: 'contentSegment',
  endpoint: ENDPOINTS.contentSegments,
  keepPreviousData: true,
  id: contentId,
}));

export const SegmentsContent = compose(
  withSegments,
  withArchivedSegments,
  withSegmentConnections,
)(SegmentsContentImpl);
